// FILE: client/src/components/navbar/MobileHeader.js
import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";

import { logout } from "../../actions/userActions";
import { getCart } from "../../actions/cartActions";
import {
  HABERDASHERY_SHOP_KEY,
  READY_MADE_SHOP_KEY,
  SHOP_PATHS,
  TEXTILE_SHOP_KEY,
} from "../../constants/shopMeta";

import "./MobileHeader.css";

export default function MobileHeader() {
  const dispatch = useDispatch();
  const location = useLocation();

  const userAuth = useSelector((state) => state.userAuth || {});
  const cartState = useSelector((state) => state.cart || {});

  const {
    loading: authLoading = false,
    userInfo = null,
    isAuthenticated = false,
  } = userAuth;

  const {
    loading: cartLoading = false,
    summary = { itemCount: 0 },
    pending = {},
  } = cartState;

  const [menuOpen, setMenuOpen] = useState(false);
  const panelRef = useRef(null);

  const identityKey = isAuthenticated
    ? userInfo?.id || userInfo?._id || "auth"
    : "guest";

  const firstName = userInfo?.name?.trim()?.split(" ")[0] || "Account";

  useEffect(() => {
    dispatch(getCart());
  }, [dispatch, identityKey]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    await dispatch(logout());
    dispatch(getCart());
  };

  const cartCount = Number(summary?.itemCount || 0);
  const isCartBusy =
    cartLoading || Boolean(pending?.summary || pending?.refresh);

  return (
    <>
      <header className="academy-mobilebar">
        <div className="academy-mobilebar-inner">
          <button
            type="button"
            className="academy-mobile-icon academy-mobile-menu-button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            {menuOpen ? <FiX /> : <FiMenu />}
          </button>

          <Link
            to="/"
            className="academy-mobile-logo"
            aria-label="Academy Costumes London home"
          >
            <span className="academy-mobile-logo-brand">ACADEMY COSTUMES</span>
            <span className="academy-mobile-logo-subbrand">LONDON</span>
          </Link>

          <div className="academy-mobile-actions">
            <Link
              to="/search"
              className="academy-mobile-icon"
              aria-label="Search"
            >
              <FiSearch />
            </Link>

            <Link
              to="/cart"
              className={`academy-mobile-icon academy-mobile-cart ${
                isCartBusy ? "is-loading" : ""
              }`}
              aria-label="Cart"
              aria-busy={isCartBusy}
            >
              <FiShoppingBag />
              {cartCount > 0 && (
                <span className="academy-mobile-badge">{cartCount}</span>
              )}
            </Link>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          className="academy-mobile-overlay"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <aside
        ref={panelRef}
        className={`academy-mobile-panel${menuOpen ? " open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <div className="academy-mobile-panel-inner">
          <div className="academy-mobile-panel-top">
            {authLoading ? (
              <div className="academy-mobile-account">Loading account…</div>
            ) : isAuthenticated && userInfo ? (
              <div className="academy-mobile-account academy-mobile-account--authed">
                <div className="academy-mobile-account-main">
                  <FiUser />
                  <span>{firstName}</span>
                </div>
                <button
                  type="button"
                  className="academy-mobile-account-link academy-mobile-logout"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/login" className="academy-mobile-account-link">
                <FiUser />
                <span>Sign in</span>
              </Link>
            )}
          </div>

          <nav className="academy-mobile-nav" aria-label="Mobile navigation">
            <NavLink to="/production">Production</NavLink>
            <NavLink to="/bespoke">Bespoke</NavLink>
            <NavLink to="/made-to-measure">Made To Measure</NavLink>
            <NavLink to="/jewelry-hire">Jewellery Hire</NavLink>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/credits">Credits</NavLink>
            <NavLink to="/services">Services</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </nav>

          <div className="academy-mobile-shoplinks">
            <div className="academy-mobile-section-label">Shop</div>

            <NavLink
              to={SHOP_PATHS[HABERDASHERY_SHOP_KEY]}
              className="academy-mobile-shop-pill"
            >
              Haberdashery
            </NavLink>
            <NavLink
              to={SHOP_PATHS[READY_MADE_SHOP_KEY]}
              className="academy-mobile-shop-pill"
            >
              Ready-Made
            </NavLink>
            <NavLink
              to={SHOP_PATHS[TEXTILE_SHOP_KEY]}
              className="academy-mobile-shop-pill"
            >
              Textiles
            </NavLink>
          </div>

          <div className="academy-mobile-utility-links">
            <Link to="/wishlist">
              <FiHeart />
              <span>Wishlist</span>
            </Link>

            <Link to="/cart">
              <FiShoppingBag />
              <span>Cart</span>
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
}
